export class IntegerValue {
    value;
    signed;
    type;
    resolved = false;
    constructor(value, signed, type) {
        this.value = value;
        this.signed = signed;
        this.type = type;
    }
    _resolve(outType, error) {
        if (this.resolved)
            return this;
        const resolveType = this.type ?? outType ?? 'int';
        const type = base.getBitsForInteger(resolveType);
        if (this.signed) {
            if (this.value > (1n << (type - 1n)) - 1n ||
                this.value < -(1n << (type - 1n))) {
                error?.('Value out of range.');
                return this;
            }
        }
        else {
            if (this.value > (1n << type) - 1n || this.value < 0n) {
                error?.('Value out of range.');
                return this;
            }
        }
        this.value = BigInt.asIntN(Number(type), this.value);
        this.resolved = true;
        return this;
    }
}
export class FloatValue {
    value;
    type;
}
export class IntegerList {
    values;
    type;
}
export function createInteger(num, signed, type) {
    return new IntegerValue(BigInt(num), signed ?? true, type ?? null)._resolve();
}
export function createFloat(num, type) {
    if (type === 'float') {
        const f32Array = new Float32Array(new ArrayBuffer(4));
        f32Array[0] = num;
        num = f32Array[0];
    }
    return {
        value: num,
        type: type ?? 'double',
    };
}
export var base;
(function (base) {
    let IntegerTypeSuffix;
    (function (IntegerTypeSuffix) {
        IntegerTypeSuffix["BYTE"] = "byte";
        IntegerTypeSuffix["SHORT"] = "short";
        IntegerTypeSuffix["INTEGER"] = "int";
        IntegerTypeSuffix["LONG"] = "long";
    })(IntegerTypeSuffix = base.IntegerTypeSuffix || (base.IntegerTypeSuffix = {}));
    let FloatTypeSuffix;
    (function (FloatTypeSuffix) {
        FloatTypeSuffix["FLOAT"] = "float";
        FloatTypeSuffix["DOUBLE"] = "double";
    })(FloatTypeSuffix = base.FloatTypeSuffix || (base.FloatTypeSuffix = {}));
    let SignedPrefix;
    (function (SignedPrefix) {
        SignedPrefix["SIGNED"] = "signed";
        SignedPrefix["UNSIGNED"] = "unsigned";
    })(SignedPrefix = base.SignedPrefix || (base.SignedPrefix = {}));
    let Sign;
    (function (Sign) {
        Sign["PLUS"] = "+";
        Sign["MINUS"] = "-";
    })(Sign = base.Sign || (base.Sign = {}));
    let Base;
    (function (Base) {
        Base[Base["HEX"] = 16] = "HEX";
        Base[Base["DEC"] = 10] = "DEC";
        Base[Base["BIN"] = 2] = "BIN";
    })(Base = base.Base || (base.Base = {}));
    function getBitsForInteger(suffix) {
        switch (suffix) {
            case IntegerTypeSuffix.BYTE:
                return 8n;
            case IntegerTypeSuffix.SHORT:
                return 16n;
            case IntegerTypeSuffix.INTEGER:
                return 32n;
            case IntegerTypeSuffix.LONG:
                return 64n;
        }
    }
    base.getBitsForInteger = getBitsForInteger;
    let BuiltinOperation;
    (function (BuiltinOperation) {
        const registry = new Map();
        function register(name, fn, length) {
            registry.set(`${length}-${name}`, fn);
        }
        BuiltinOperation.register = register;
        function get(name, length) {
            return registry.get(`${length}-${name}`);
        }
        BuiltinOperation.get = get;
        register('bool', (error, val) => {
            if (typeof val === 'boolean')
                return val;
            if (val instanceof IntegerValue) {
                return val.value !== 0n;
            }
            if (val instanceof FloatValue) {
                return val.value !== 0;
            }
            error('Expect number or boolean');
        }, 1);
        register('uuid', (error, uuid) => {
            if (typeof uuid !== 'string') {
                error('Expect string uuid');
                return ''; // will not return
            }
            if (uuid.length > 36)
                error('Expect string uuid');
            const split = uuid.split('-');
            if (split.length !== 5)
                error('Expect string uuid');
            const uuidNum = ((BigInt(`0x${split[0]}`) & 0xffffffffn) << 96n) |
                ((BigInt(`0x${split[1]}`) & 0xffffn) << 80n) |
                ((BigInt(`0x${split[2]}`) & 0xffffn) << 64n) |
                ((BigInt(`0x${split[3]}`) & 0xffffn) << 48n) |
                (BigInt(`0x${split[4]}`) & 0xffffffffffffn);
            return {
                values: [
                    BigInt.asIntN(32, uuidNum >> 96n),
                    BigInt.asIntN(32, uuidNum >> 64n),
                    BigInt.asIntN(32, uuidNum >> 32n),
                    BigInt.asIntN(32, uuidNum),
                ],
                type: 'int',
            };
        }, 1);
    })(BuiltinOperation = base.BuiltinOperation || (base.BuiltinOperation = {}));
    function convertToIntTypeSuffix(suffix) {
        switch (suffix) {
            case 'b':
            case 'B':
                return IntegerTypeSuffix.BYTE;
            case 's':
            case 'S':
                return IntegerTypeSuffix.SHORT;
            case 'i':
            case 'I':
                return IntegerTypeSuffix.INTEGER;
            case 'l':
            case 'L':
                return IntegerTypeSuffix.LONG;
        }
    }
    base.convertToIntTypeSuffix = convertToIntTypeSuffix;
    function convertToFloatTypeSuffix(suffix) {
        switch (suffix) {
            case 'f':
            case 'F':
                return FloatTypeSuffix.FLOAT;
            case 'd':
            case 'D':
                return FloatTypeSuffix.DOUBLE;
        }
        return null;
    }
    base.convertToFloatTypeSuffix = convertToFloatTypeSuffix;
    function checkNum(num, error) {
        if (num.startsWith('_') || num.endsWith('_'))
            error('Underscore is not allowed');
        return num;
    }
    base.checkNum = checkNum;
    function cleanNumber(num) {
        const needUnderscoreRemoval = num.includes('_');
        if (needUnderscoreRemoval)
            num = num.replace(/_/g, '');
        return num;
    }
    function convertNum(num, error) {
        const signedPrefix = num.suffix
            ? num.suffix.signed
            : num.base === 10
                ? SignedPrefix.SIGNED
                : SignedPrefix.UNSIGNED;
        const signed = signedPrefix === SignedPrefix.SIGNED;
        if (!signed && num.sign === Sign.MINUS)
            error('Expected non negative number');
        num.value = cleanNumber(num.value);
        const converted = `${num.base === Base.HEX ? '0x' : num.base === Base.BIN ? '0b' : ''}${num.value}`;
        let integer = BigInt(converted);
        if (signed) {
            if (num.sign === Sign.MINUS)
                integer = -integer;
            return new IntegerValue(integer, signed, num.suffix?.type ?? null);
        }
        else {
            return new IntegerValue(integer, signed, num.suffix?.type ?? null);
        }
    }
    base.convertNum = convertNum;
    function convertFloat(num, error) {
        num.integer = cleanNumber(num.integer);
        num.frac = cleanNumber(num.frac);
        if (num.exp)
            num.exp.value = cleanNumber(num.exp.value);
        const converted = `${num.sign}${num.integer}.${num.frac}${num.exp ? `e${num.exp.sign}${num.exp.value}` : ''}`;
        let float = parseFloat(converted);
        if (!isFinite(float))
            error('Infinity not allowed');
        const suf = num.suf ? num.suf : FloatTypeSuffix.DOUBLE;
        return createFloat(float, suf);
    }
    base.convertFloat = convertFloat;
    function convertHexString(str, error) {
        const hex = parseInt(str, 16);
        if (hex > 0x10ffff)
            error(`Invalid codepoint U+${hex.toString(16).toUpperCase().padStart(8, '0')}`);
        return String.fromCodePoint(hex);
    }
    base.convertHexString = convertHexString;
    function convertUnicodeNameString(str, error) {
        return str; // TODO
    }
    base.convertUnicodeNameString = convertUnicodeNameString;
    function convertUnquotedOrBuiltin(str, argv, error) {
        if (str.charAt(0).match(/[-+.0-9]/))
            error('Invalid unquoted start');
        if (argv) {
            const op = BuiltinOperation.get(str, argv.length);
            if (op) {
                const result = op(error, ...argv);
                if (result)
                    return result;
            }
            error('No such operation');
        }
        if (str.toLowerCase() === 'true')
            return true;
        if (str.toLowerCase() === 'false')
            return false;
        return str;
    }
    base.convertUnquotedOrBuiltin = convertUnquotedOrBuiltin;
    function convertIntList(prefix, list, error) {
        const type = convertToIntTypeSuffix(prefix);
        if (list
            .filter(l => !!l.type)
            .some(l => getBitsForInteger(l.type) >
            getBitsForInteger(type)))
            error('Invalid array element type');
        return {
            type: type,
            values: list.map(l => l._resolve(type).value),
        };
    }
    base.convertIntList = convertIntList;
})(base || (base = {}));
//# sourceMappingURL=snbt-parser.base.js.map